import { defineComponent, ref, provide, reactive, toRefs, getCurrentInstance, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import PageLoading from '@/components/common/PageLoading.vue';
import Swiper from '@/components/layout/Swiper.vue';
import SearchBar2 from '@/components/layout/SearchBar2.vue';
import Header2 from '@/components/layout/header2.vue';
import Footer from '@/components/layout/footer.vue';
import Menu from '@/components/layout/menu.vue';
import bus from 'vue3-eventbus';
import SideComponent from '@/components/common/SideComponent.vue';
import Loading from '@/components/common/Loading.vue';
import { buildIntlVideoUrl, buildIntlDetailUrl } from '@/utils/seoUrl.js';
import { SiteInfo } from '@/api/common';
import { getImageSize } from '@/utils/utils';

export default defineComponent({
	components: {
		PageLoading,
		Header2,
		Menu,
		Swiper,
		SearchBar2,
		Footer,
		SideComponent,
		Loading,
	},
	setup() {
		provide('logoColor', 'white');
		const { proxy } = getCurrentInstance();
		const route = useRoute();
		const router = useRouter();
		const data = reactive({
			isLoading: true,
			showHeader: true,
			loginStyle: 'min-width:141px;',
			mobileIconStyle: 'color:rgba(223, 58, 8, 1);',
			showListIcon: true,
			showNews: false,
			showVisual: false,
			showTabs: false,
			activeTab: route.query.tab === 'visual' ? 'visual' : 'news',
			picLoaded: false,
			videoLoaded: false,
			picList: [],
			videoList: [],
			videoColumns: [[], [], []],
			picPage: 1,
			videoPage: 1,
			picNomore: true,
			videoNomore: true,
			isPicLoading: false,
			isVideoLoading: false,
			isMobile: false,
			international_sliders: [],
			pictype: 1,
			videotype: 2,
		});

		const showMenu = ref(proxy.$menus);
		provide('showMenu', showMenu);

		const store = useStore();

		const getSiteInfo = () => {
			return new Promise((resolve) => {
				setTimeout(() => {
					store.dispatch('common/fetchSiteInfo').then(async (res) => {
						let sliders = (res && res.international_sliders) || [];
						if (!sliders.length) {
							try {
								const fresh = await SiteInfo();
								sliders = fresh.international_sliders || [];
								if (sliders.length) {
									const cached = {
										...res,
										international_sliders: sliders,
									};
									sessionStorage.setItem('siteInfo', JSON.stringify(cached));
								}
							} catch {
								// 保持空轮播
							}
						}
						data.international_sliders = sliders;
						bus.emit('isLoading', false);
						resolve();
					}).catch(() => {
						bus.emit('isLoading', false);
						resolve();
					});
				}, 200);
			});
		};

		const syncTabsFromData = () => {
			data.showNews = data.picList.length > 0;
			data.showVisual = data.videoList.length > 0;
			data.showTabs = data.showNews || data.showVisual;

			let tab = route.query.tab === 'visual' ? 'visual' : 'news';
			if (tab === 'news' && !data.showNews) {
				tab = data.showVisual ? 'visual' : 'news';
			} else if (tab === 'visual' && !data.showVisual) {
				tab = data.showNews ? 'news' : 'visual';
			}
			if (data.showNews && !data.showVisual) {
				tab = 'news';
			} else if (data.showVisual && !data.showNews) {
				tab = 'visual';
			}

			data.activeTab = data.showTabs ? tab : null;
			if (data.showTabs && data.activeTab) {
				router.replace({ query: { ...route.query, tab: data.activeTab } }).catch(() => {});
			}
		};

		const resetAndLoadCategory = async () => {
			data.isLoading = true;
			data.picList = [];
			data.videoList = [];
			data.videoColumns = [[], [], []];
			data.picPage = 1;
			data.videoPage = 1;
			data.picNomore = true;
			data.videoNomore = true;
			data.picLoaded = false;
			data.videoLoaded = false;
			data.showNews = false;
			data.showVisual = false;
			data.showTabs = false;
			data.activeTab = route.query.tab === 'visual' ? 'visual' : 'news';
			try {
				await Promise.all([
					internationalPicList(1),
					internationalVideoList(1),
				]);
				syncTabsFromData();
			} finally {
				data.isLoading = false;
				bus.emit('isLoading', false);
			}
		};

		const internationalPicList = (page = 1, size = 12) => {
			data.picPage = page;
			data.isPicLoading = true;
			return store.dispatch('common/InternationalList', {
				type: 1,
				cate_id: route.params.id,
				page,
				size,
			}).then((res) => {
				const rows = res.data || [];
				if (rows.length > 0) {
					rows.forEach((item) => {
						item.title = item.title.replace(/#/g, '<br>');
					});
					if (page === 1) {
						data.picList = rows;
					} else {
						data.picList = data.picList.concat(rows);
					}
					data.picNomore = rows.length < res.pagination.pageSize;
				} else {
					if (page === 1) {
						data.picList = [];
					}
					data.picNomore = true;
				}
				data.picPage += 1;
				data.picLoaded = true;
				data.isPicLoading = false;
				bus.emit('showFooter', true);
			}).catch(() => {
				if (page === 1) {
					data.picList = [];
				}
				data.picLoaded = true;
				data.isPicLoading = false;
			});
		};

		const getVideoRatio = (item) => {
			const ratioW = parseFloat(item.ratio_w) || parseFloat(item.thumb_width) || 16;
			const ratioH = parseFloat(item.ratio_h) || parseFloat(item.thumb_height) || 9;
			return { ratioW, ratioH };
		};

		const hasVideoRatio = (item) => {
			const w = parseFloat(item.ratio_w) || parseFloat(item.thumb_width);
			const h = parseFloat(item.ratio_h) || parseFloat(item.thumb_height);
			return w > 0 && h > 0;
		};

		const ensureVideoRatios = async (videoList) => {
			await Promise.all((videoList || []).map(async (item) => {
				if (hasVideoRatio(item) || !item.thumb) return;
				try {
					const size = await getImageSize(item.thumb);
					if (size.width > 0 && size.height > 0) {
						item.ratio_w = size.width;
						item.ratio_h = size.height;
					}
				} catch {
					// 读不到封面尺寸时沿用 16:9
				}
			}));
		};

		const distributeVideos = (videoList) => {
			videoList = videoList || [];
			const isMobileLayout = document.documentElement.clientWidth < 890;
			if (isMobileLayout) {
				data.videoColumns[0].splice(0, data.videoColumns[0].length, ...videoList);
				data.videoColumns[1].splice(0, data.videoColumns[1].length);
				data.videoColumns[2].splice(0, data.videoColumns[2].length);
				return;
			}
			const columns = [[], [], []];
			const columnHeights = [0, 0, 0];
			const width = 423;
			videoList.forEach((item) => {
				const minIndex = columnHeights.indexOf(Math.min(...columnHeights));
				columns[minIndex].push(item);
				const { ratioW, ratioH } = getVideoRatio(item);
				const videoHeight = width * ratioH / ratioW;
				columnHeights[minIndex] += videoHeight + 50;
			});
			data.videoColumns[0].splice(0, data.videoColumns[0].length, ...columns[0]);
			data.videoColumns[1].splice(0, data.videoColumns[1].length, ...columns[1]);
			data.videoColumns[2].splice(0, data.videoColumns[2].length, ...columns[2]);
		};

		const getVideoAspectStyle = (item) => {
			const { ratioW, ratioH } = getVideoRatio(item);
			return {
				objectFit: 'cover',
				width: '100%',
				height: 'auto',
				aspectRatio: `${ratioW} / ${ratioH}`,
				display: 'block',
			};
		};

		const internationalVideoList = (page = 1, size = 12) => {
			data.videoPage = page;
			data.isVideoLoading = true;
			return store.dispatch('common/InternationalList', {
				type: 2,
				cate_id: route.params.id,
				page,
				size,
			}).then(async (res) => {
				const rows = res.data || [];
				if (rows.length > 0) {
					rows.forEach((item) => {
						item.title = item.title.replace(/#/g, '<br>');
					});
					if (page === 1) {
						data.videoList = rows;
					} else {
						data.videoList = data.videoList.concat(rows);
					}
					await ensureVideoRatios(data.videoList);
					distributeVideos(data.videoList);
					data.videoNomore = rows.length < res.pagination.pageSize;
				} else {
					if (page === 1) {
						data.videoList = [];
						distributeVideos([]);
					}
					data.videoNomore = true;
				}
				data.videoPage += 1;
				data.videoLoaded = true;
				data.isVideoLoading = false;
				bus.emit('showFooter', true);
			}).catch(() => {
				if (page === 1) {
					data.videoList = [];
					distributeVideos([]);
				}
				data.videoLoaded = true;
				data.isVideoLoading = false;
			});
		};

		const switchTab = async (tab) => {
			if (tab !== 'news' && tab !== 'visual') return;
			if (tab === 'news' && !data.showNews) return;
			if (tab === 'visual' && !data.showVisual) return;
			data.activeTab = tab;
			router.replace({ query: { ...route.query, tab } }).catch(() => {});
		};

		const viewBox = ref(null);
		const scroll = (val) => {
			if (!viewBox.value) return;
			viewBox.value.scrollIntoView({
				behavior: 'smooth',
				block: val === 'top' ? 'start' : 'end',
				inline: 'center',
			});
		};

		onMounted(async () => {
			try {
				await getSiteInfo();
				await resetAndLoadCategory();
			} finally {
				setTimeout(() => {
					if (viewBox.value) {
						viewBox.value.scrollIntoView({
							behavior: 'smooth',
							block: 'start',
							inline: 'center',
						});
					}
				}, 100);
			}
		});

		// 同组件切换 /international/cate/:id（如 世界在青岛 ↔ 青岛在世界）需重新拉数
		watch(
			() => route.params.id,
			(id, prev) => {
				if (!id || String(id) === String(prev)) return;
				resetAndLoadCategory();
			},
		);

		return {
			...toRefs(data),
			viewBox,
			scroll,
			switchTab,
			internationalPicList,
			internationalVideoList,
			getVideoAspectStyle,
		};
	},
	mounted() {
		this.$nextTick(() => {
			this.isMobile = document.documentElement.clientWidth < 768;
		});
	},
	methods: {
		loadMore(type) {
			if (type === 1) {
				this.internationalPicList(this.picPage);
			} else if (type === 2) {
				this.internationalVideoList(this.videoPage);
			}
		},
		toVideoDetail(id) {
			const href = this.$router.resolve(buildIntlVideoUrl(id));
			window.open(href.href, '_blank');
		},
		toDetail(id) {
			const href = this.$router.resolve(buildIntlDetailUrl(id));
			window.open(href.href, '_blank');
		},
	},
});
